// src/services/projectService.js
import api from '../config/axios';

const BASE_PATH = '/projects';

// ============= Query Operations =============

/**
 * Get all active projects
 */
export const getAllProjects = async () => {
  const response = await api.get(BASE_PATH);
  return response.data;
};

/**
 * Get all active projects with pagination
 */
export const getAllProjectsPaged = async (page = 0, size = 10, sort = 'createdAt,desc') => {
  const response = await api.get(`${BASE_PATH}/paged`, {
    params: { page, size, sort }
  });
  return response.data;
};

/**
 * Get project by ID
 */
export const getProjectById = async (id) => {
  const response = await api.get(`${BASE_PATH}/${id}`);
  return response.data;
};

/**
 * Get project by ID with full details (segments/sites included)
 * GET /api/projects/{id}/details
 * 
 * @param {number} id - Project ID
 * 
 * Returns: ProjectResponse with foSegments[] or towerSites[] populated
 */
export const getProjectDetails = async (id) => {
  const response = await api.get(`/projects/${id}/details`);
  return response.data;
};

/**
 * Get project by ID WITH DETAILS (includes sites/segments arrays)
 * Used for expand/detail views
 */
export const getProjectByIdWithDetails = async (id) => {
  const response = await api.get(`${BASE_PATH}/${id}/details`);
  return response.data;
};

/**
 * Get project by code
 */
export const getProjectByCode = async (projectCode) => {
  const response = await api.get(`${BASE_PATH}/code/${projectCode}`);
  return response.data;
};

/**
 * Search projects
 */
export const searchProjects = async (query, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/search`, {
    params: { q: query, page, size }
  });
  return response.data;
};

// ============= Command Operations =============

/**
 * Create new project from SPK
 */
export const createProject = async (projectData) => {
  const response = await api.post(BASE_PATH, projectData);
  return response.data;
};

/**
 * Update existing project
 */
export const updateProject = async (id, projectData) => {
  const response = await api.put(`${BASE_PATH}/${id}`, projectData);
  return response.data;
};

/**
 * Soft delete project
 */
export const softDeleteProject = async (id) => {
  await api.delete(`${BASE_PATH}/${id}`);
};

/**
 * Delete project (soft delete)
 * DELETE /api/projects/{id}
 * 
 * @param {number} id - Project ID
 * 
 * Returns: void (204 No Content)
 */
export const deleteProject = async (id) => {
  const response = await api.delete(`/projects/${id}`);
  return response.data;
};

/**
 * Recalculate project totals
 */
export const recalculateProjectTotals = async (id) => {
  const response = await api.post(`${BASE_PATH}/${id}/recalculate`);
  return response.data;
};

// ============= Helper Functions =============

/**
 * Project Type Enum (match backend)
 */
export const ProjectType = {
  TOWER: 'TOWER',
  FO: 'FO',
  TOWER_COLO: 'TOWER_COLO'
};

/**
 * Project Status Enum (match backend)
 */
export const ProjectStatus = {
  STARTED: 'STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  ON_HOLD: 'ON_HOLD',
  COMPLETED: 'COMPLETED',
  CLOSED: 'CLOSED',
  DROP: 'DROP'
};

/**
 * Get project type display name
 */
export const getProjectTypeDisplayName = (type) => {
  const typeMap = {
    TOWER: 'Tower',
    FO: 'Fiber Optic'
  };
  return typeMap[type] || type;
};

/**
 * Get project type badge class
 */
export const getProjectTypeBadgeClass = (type) => {
  const colorMap = {
    TOWER: 'bg-purple-100 text-purple-800',
    FO: 'bg-green-100 text-green-800'
  };
  return colorMap[type] || 'bg-gray-100 text-gray-800';
};

/**
 * Get status display name
 */
export const getStatusDisplayName = (status) => {
  const statusMap = {
    STARTED: 'Started',
    IN_PROGRESS: 'In Progress',
    ON_HOLD: 'On Hold',
    COMPLETED: 'Completed',
    CLOSED: 'Closed',
    DROP: 'Dropped'
  };
  return statusMap[status] || status;
};

/**
 * Get status badge class
 */
export const getStatusBadgeClass = (status) => {
  const colorMap = {
    STARTED: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
    ON_HOLD: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CLOSED: 'bg-purple-100 text-purple-800',
    DROP: 'bg-red-100 text-red-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Calculate days remaining
 */
export const calculateDaysRemaining = (targetEndDate) => {
  if (!targetEndDate) return null;
  
  const today = new Date();
  const target = new Date(targetEndDate);
  const diffTime = target - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Format currency (Indonesian Rupiah)
 */
export const formatCurrency = (amount) => {
  if (!amount) return 'Rp 0';
  
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Calculate delivery period end date
 */
export const calculateTargetEndDate = (spkDate, deliveryPeriod) => {
  if (!spkDate || !deliveryPeriod) return null;
  
  const date = new Date(spkDate);
  date.setMonth(date.getMonth() + deliveryPeriod);
  
  return date.toISOString().split('T')[0]; // Return YYYY-MM-DD
};

export default {
  getAllProjects,
  getAllProjectsPaged,
  getProjectById,
  getProjectDetails,
  getProjectByIdWithDetails,  // ✅ Add this
  getProjectByCode,
  searchProjects,
  createProject,
  updateProject,
  softDeleteProject,
  deleteProject,
  recalculateProjectTotals,
  ProjectType,
  ProjectStatus,
  getProjectTypeDisplayName,
  getProjectTypeBadgeClass,
  getStatusDisplayName,
  getStatusBadgeClass,
  calculateDaysRemaining,
  formatCurrency,
  calculateTargetEndDate
};